import { View, ViewData, mustBeHTMLElementOfType } from "silver";
import { BookBundleInterface, BookBundleService } from "shared/Services/BookBundleService";
import { MyonLanguageLoader, BooksLang } from "Lang";
import { ContextualPopup, ContextualPopupBehavior } from "shared/Components/ContextualPopup";

const HTML: string = `<div class="bookBundle" tabindex="0">
	<h2 class="bookBundle_header">{{ lang.HEADER_ADD_TO_BUNDLE }}</h2>
	<form class="bookBundle_createForm" data-myon-on-submit="handleCreate">
		<input class="input bookBundle_input" data-myon-model="createBundleTitle: string" placeholder="{{ lang.PLACEHOLDER_NEW_BUNDLE }}" />
	</form>
	<ul class="bookBundle_list">
		<li class="bookBundle_listItem">
			<label for="book-bundle-mylist" class="bookBundle_label">{{ lang.MY_FAVORITES }}</label>
			<button id="book-bundle-mylist" type="button" class="link" aria-pressed="{{ defaultSet | toBoolString }}" data-myon-on-click="toggleDefault">
				<i data-myon-attr-class="link_icon mycon {{ defaultSet? | '-mylist-remove-small' }} {{ !defaultSet? | '-mylist-add-small' }}"></i>
				<span class="g_screenReader">
					{{ defaultSet | toBoolString }}
				</span>
			</button>
		</li>
		<li class="bookBundle_listItem" data-myon-template="options">
			<label for="book-bundle-checkbox-{{ id }}" class="bookBundle_label">{{ title }}</label>
			<input id="book-bundle-checkbox-{{ id }}" type="checkbox" class="checkbox -smol" data-myon-prop-checked="{{ id | bookInSet }}" data-myon-on-change="handleCheckboxChange" />
		</li>
	</ul>
</div>`;

type BookBundleMenuViewData = ViewData & {
	defaultSet: boolean;
	options: BookBundleInterface[];
	createBundleTitle: string;
	lang: BooksLang;
}

export class BookBundleMenu {

	constructor(
		private readonly bookBundleService: BookBundleService,
		private readonly langLoader: MyonLanguageLoader,
	) { }

	public async showMenu(target: HTMLElement, bookId: number): Promise<void> {
		const bundleData = await this.bookBundleService.getBundleData();
		const bundleAndBookArray = Object.values(bundleData.bundles);

		const initialSet = new Set<number>(bundleAndBookArray
			.filter(({ books }) => books.includes(bookId))
			.map(({ bundle }) => bundle.id));

		const bundles = bundleAndBookArray.map(({ bundle }) => bundle);

		const nonDefaultBundles = bundles.filter(o => !o.isDefault);
		const defaultBundle = bundles.find(o => o.isDefault);
		if (!defaultBundle) {
			throw new Error("No default option");
		}

		const lang = await this.langLoader.load("Books")
		await this.prompBundleMenu(bookId, lang, target, defaultBundle, nonDefaultBundles, initialSet);
	}

	private prompBundleMenu(
		bookId: number,
		lang: BooksLang,
		target: HTMLElement,
		defaultOption: BookBundleInterface,
		options: BookBundleInterface[],
		initialSet: Set<number>
	): Promise<void> {

		return new Promise(resolve => {

			// sort favorites to top, then bundles alphabetically
			options.sort((a, b) => a.title.localeCompare(b.title));

			const view = new View<BookBundleMenuViewData>(HTML, {
				toggleDefault: () => {
					view.dataset.defaultSet = !view.dataset.defaultSet;
					this.bookBundleService.setBookIdsInFavorites([ bookId ], view.dataset.defaultSet);
				},
				bookInSet:            (bookBundleId: number) => initialSet.has(bookBundleId),
				toBoolString:         (v) => v ? "true" : "false",
				handleCheckboxChange: (e: Event, bundle: BookBundleInterface) => {
					const input = mustBeHTMLElementOfType(e.target, "input");
					this.bookBundleService.setBookIdsInBundle([ bookId ], bundle.id, input.checked);
				},
				handleCreate: async (e: Event) => {
					e.preventDefault();
					const title = view.dataset.createBundleTitle;
					view.dataset.createBundleTitle = "";
					const bundle = await this.bookBundleService.createBundle(title);
					options.push(bundle);
					options.sort((a, b) => a.title.localeCompare(b.title));
					view.dataset.options = options;
				},
			}, {
				defaultSet:        initialSet.has(defaultOption.id),
				options,
				lang,
				createBundleTitle: "",
			});

			const contextMenu = new ContextualPopup(view.element);
			contextMenu.setContext(target, ContextualPopupBehavior.NONE);
			contextMenu.onHide(() => {
				setTimeout(() => {
					if (contextMenu.element.parentElement) {
						contextMenu.element.parentElement.removeChild(contextMenu.element);
					}
				}, 1000);
				resolve();
			});
			contextMenu.show();
		});

	}
}
